import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, KeyRound } from "lucide-react";
import Header from "../components/Header";
import Marquee from "../components/Marquee";
import EventCountdown from "../components/EventCountdown";
import MembersWall from "../components/MembersWall";
import PromoBanner from "../components/PromoBanner";
import { useLenis } from "../lib/useLenis";
import { useT } from "../lib/i18n";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/878a64be-86ab-4267-879d-add4365a5f54/images/cd43aa06af5cb611b9d191480598135bf285fea781244b547f9ed1252cda2112.png";

/* ---------- Motion primitives ---------- */

const EASE = [0.22, 1, 0.36, 1];

const maskLine = {
    hidden: { y: "115%" },
    show: (i = 0) => ({
        y: "0%",
        transition: { duration: 1.15, ease: EASE, delay: 0.15 + i * 0.09 },
    }),
};

const softRise = {
    hidden: { opacity: 0, y: 26 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, ease: EASE, delay: 0.1 + i * 0.08 },
    }),
};

/* Line-mask reveal wrapper — hides overflow, animates child up from below. */
function Reveal({ children, delay = 0, className = "", testid }) {
    return (
        <span className={`line-mask ${className}`} data-testid={testid}>
            <motion.span
                className="block"
                variants={maskLine}
                initial="hidden"
                animate="show"
                custom={delay}
            >
                {children}
            </motion.span>
        </span>
    );
}

/* ---------- HERO ---------- */

function Hero({ t }) {
    // mouse parallax on the giant title
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const sx = useSpring(x, { stiffness: 60, damping: 20, mass: 0.6 });
    const sy = useSpring(y, { stiffness: 60, damping: 20, mass: 0.6 });

    // scroll parallax
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    // clock (Stockholm) — real-time detail nightlife guests love
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const stockholmTime = now.toLocaleTimeString("sv-SE", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Stockholm",
    });

    const onMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        x.set(px * 22);
        y.set(py * 14);
    };
    const onLeave = () => { x.set(0); y.set(0); };

    return (
        <section
            ref={heroRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative min-h-[100dvh] flex flex-col justify-end overflow-hidden"
            data-testid="hero-section"
        >
            {/* Parallax background */}
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src={HERO_BG}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ filter: "saturate(115%) brightness(0.48) contrast(1.08)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-bg-primary" />
                <div className="absolute inset-0 bg-[radial-gradient(120%_70%_at_50%_110%,rgba(255,180,60,0.22),transparent_60%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(60%_45%_at_15%_10%,rgba(20,90,55,0.30),transparent_70%)] mix-blend-screen" />
                <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-bg-primary to-transparent" />
            </motion.div>

            {/* Top corner tags — editorial */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.9, ease: EASE }}
                className="absolute top-24 left-6 lg:left-12 z-10"
            >
                <div className="tag-corner" data-testid="tag-since">Est. MMXXV · Kungsträdgården</div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.55, duration: 0.9, ease: EASE }}
                className="absolute top-24 right-6 lg:right-12 z-10 flex items-center gap-3"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-gold pulse-dot" />
                <div className="tag-corner border-none px-0 bg-transparent" data-testid="tag-clock">
                    STHLM · {stockholmTime}
                </div>
            </motion.div>

            {/* Hero content */}
            <motion.div
                style={{ y: titleY, opacity }}
                className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-24 w-full"
            >
                {/* Signature title — masked line-by-line reveal, parallax on mouse */}
                <motion.div
                    style={{ x: sx, y: sy }}
                    className="select-none pointer-events-none"
                >
                    <div className="line-mask" data-testid="hero-title">
                        <motion.h1
                            variants={maskLine}
                            initial="hidden"
                            animate="show"
                            custom={0}
                            className="hero-word hero-word--fill text-[22vw] sm:text-[18vw] md:text-[14vw] lg:text-[13rem] xl:text-[15rem]"
                        >
                            Scotts
                        </motion.h1>
                    </div>
                    <div className="line-mask -mt-[3vw]" data-testid="hero-title-2">
                        <motion.h1
                            variants={maskLine}
                            initial="hidden"
                            animate="show"
                            custom={1.1}
                            className="hero-word hero-word--stroke text-[22vw] sm:text-[18vw] md:text-[14vw] lg:text-[13rem] xl:text-[15rem]"
                        >
                            Club<span className="amp-mark inline-block ml-3 sm:ml-6 align-baseline text-gold" style={{ WebkitTextStroke: 0 }}>✦</span>
                        </motion.h1>
                    </div>
                </motion.div>

                {/* Subtitle — editorial */}
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
                    <div className="lg:col-span-5">
                        <div className="line-mask">
                            <motion.div
                                variants={maskLine}
                                initial="hidden"
                                animate="show"
                                custom={2}
                                className="label-eyebrow flex items-center gap-3"
                            >
                                <span className="w-8 h-px bg-gold" />
                                {t("landing.eyebrow")}
                            </motion.div>
                        </div>
                        <div className="line-mask mt-4">
                            <motion.p
                                variants={maskLine}
                                initial="hidden"
                                animate="show"
                                custom={2.3}
                                className="font-serif italic text-ink-primary text-2xl md:text-3xl leading-[1.1]"
                                data-testid="hero-subtitle"
                            >
                                {t("landing.hero2")}
                            </motion.p>
                        </div>
                    </div>

                    <div className="lg:col-span-5 lg:col-start-7">
                        <motion.p
                            variants={softRise}
                            initial="hidden"
                            animate="show"
                            custom={4}
                            className="text-ink-secondary text-base md:text-lg leading-relaxed max-w-md font-light"
                        >
                            {t("landing.hero_sub")}
                        </motion.p>

                        <motion.div
                            variants={softRise}
                            initial="hidden"
                            animate="show"
                            custom={5}
                            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
                        >
                            <Link
                                to="/join"
                                data-testid="hero-cta-join"
                                className="group btn-gold inline-flex items-center justify-between gap-4 pl-6 pr-3 py-3 rounded-full font-medium tracking-wide"
                            >
                                <span className="text-sm tracking-[0.18em] uppercase">{t("landing.cta_primary")}</span>
                                <span className="w-9 h-9 rounded-full bg-black/25 flex items-center justify-center transition-transform group-hover:rotate-45">
                                    <ArrowUpRight size={16} strokeWidth={1.6} />
                                </span>
                            </Link>
                            <Link
                                to="/member"
                                data-testid="hero-cta-member"
                                className="group inline-flex items-center justify-center gap-2 px-6 py-3 border border-gold/40 text-gold rounded-full hover:bg-gold/10 hover:border-gold/70 transition-all text-sm tracking-[0.18em] uppercase"
                            >
                                <KeyRound size={14} />
                                {t("landing.cta_secondary")}
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-ink-muted text-[10px] tracking-[0.4em] uppercase z-10"
            >
                <span>Scroll</span>
                <motion.div
                    animate={{ scaleY: [0.4, 1, 0.4], transformOrigin: "top" }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    className="w-px h-12 bg-gradient-to-b from-gold to-transparent"
                />
            </motion.div>
        </section>
    );
}

/* ---------- MARQUEE STRIP ---------- */

function MarqueeStrip() {
    return (
        <section className="relative py-6 border-y border-gold/15 bg-black/25 overflow-hidden" data-testid="marquee-strip">
            <Marquee
                dataTestId="marquee-tape"
                speed={55}
                items={[
                    "Members Only",
                    "Kungsträdgården",
                    "By Application",
                    "MMXXV",
                    "Stockholm",
                    "The Velvet Rope",
                ]}
            />
        </section>
    );
}

/* ---------- MANIFESTO CHAPTERS ---------- */

const CHAPTERS = [
    {
        n: "01",
        titleKey: "landing.section_about_eyebrow",
        headline: "landing.section_about_title",
        body: "landing.section_about_body",
        pull: "The city's most curated crowd.",
    },
    {
        n: "02",
        titleKey: "landing.section_perks_eyebrow",
        headline: "landing.section_perks_title",
        body: null,
        perks: true,
    },
    {
        n: "03",
        titleKey: "landing.section_how_eyebrow",
        headline: "landing.section_how_title",
        body: null,
        process: true,
    },
];

function ChapterHeader({ n, title, headline, delay = 0 }) {
    return (
        <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-3 sm:col-span-2">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE, delay }}
                    className="chapter-num"
                >
                    {n}
                </motion.div>
            </div>
            <div className="col-span-9 sm:col-span-10">
                <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.1, ease: EASE, delay: delay + 0.1 }}
                    className="h-px bg-gradient-to-r from-gold/70 via-gold/30 to-transparent origin-left mb-6"
                />
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE, delay: delay + 0.2 }}
                    className="label-eyebrow"
                >
                    {title}
                </motion.div>
                <div className="line-mask mt-3">
                    <motion.h2
                        initial={{ y: "110%" }}
                        whileInView={{ y: "0%" }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.05, ease: EASE, delay: delay + 0.25 }}
                        className="font-serif font-medium text-4xl sm:text-5xl md:text-6xl leading-[1.03] tracking-tight text-balance max-w-4xl"
                    >
                        {headline}
                    </motion.h2>
                </div>
            </div>
        </div>
    );
}

function ChapterOne({ t }) {
    // photo parallax
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

    return (
        <section
            ref={ref}
            className="relative py-24 md:py-32 lg:py-40 border-t border-emerald-line/50"
            data-testid="chapter-01"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <ChapterHeader
                    n={CHAPTERS[0].n}
                    title={t(CHAPTERS[0].titleKey)}
                    headline={t(CHAPTERS[0].headline)}
                />

                {/* Body + Editorial photo */}
                <div className="mt-16 md:mt-24 grid grid-cols-12 gap-8 lg:gap-16">
                    {/* Photo (clipped, parallax, spotlight) */}
                    <div className="col-span-12 lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 60 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-120px" }}
                            transition={{ duration: 1.2, ease: EASE }}
                            className="relative photo-clip aspect-[16/11]"
                        >
                            <motion.img
                                src={HERO_BG}
                                alt="John Scotts Kungsträdgården"
                                className="w-full h-full object-cover"
                                style={{ y: imgY }}
                            />
                            <div className="absolute bottom-6 left-6 z-10">
                                <div className="tag-corner">Hamngatan 19 · Stockholm</div>
                            </div>
                            <div className="absolute top-6 right-6 z-10">
                                <div className="tag-corner">Frame · 01</div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Body copy */}
                    <div className="col-span-12 lg:col-span-5 flex flex-col justify-end">
                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                            className="text-ink-secondary text-base md:text-lg leading-[1.75] font-light"
                        >
                            {t(CHAPTERS[0].body)}
                        </motion.p>

                        <motion.blockquote
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                            className="mt-10 pl-6 border-l border-gold/40 font-serif italic text-xl md:text-2xl text-ink-primary leading-snug max-w-md"
                        >
                            &ldquo;{CHAPTERS[0].pull}&rdquo;
                        </motion.blockquote>

                        {/* Live stat pair */}
                        <div className="mt-12 grid grid-cols-2 gap-8" data-testid="live-stats">
                            <div>
                                <div className="stat-num">48h</div>
                                <div className="label-eyebrow mt-2">Review window</div>
                            </div>
                            <div>
                                <div className="stat-num">1×</div>
                                <div className="label-eyebrow mt-2">Scan at door</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ChapterTwo({ t }) {
    const perks = [
        { key: "perk1", num: "I" },
        { key: "perk2", num: "II" },
        { key: "perk3", num: "III" },
    ];
    return (
        <section
            className="relative py-24 md:py-32 lg:py-40 border-t border-emerald-line/50 bg-bg-secondary noise overflow-hidden"
            data-testid="chapter-02"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <ChapterHeader
                    n={CHAPTERS[1].n}
                    title={t(CHAPTERS[1].titleKey)}
                    headline={t(CHAPTERS[1].headline)}
                />

                <div className="mt-16 md:mt-24 grid md:grid-cols-3 gap-px bg-gold/10">
                    {perks.map((p, i) => (
                        <motion.div
                            key={p.key}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: EASE, delay: 0.1 * i }}
                            className="relative bg-bg-primary p-10 md:p-12 group hover:bg-bg-surface transition-colors duration-500"
                            data-testid={`perk-card-${i}`}
                        >
                            {/* Roman numeral */}
                            <div className="flex items-start justify-between mb-16">
                                <span className="font-serif italic text-gold text-2xl">{p.num}</span>
                                <ArrowUpRight
                                    size={20}
                                    strokeWidth={1}
                                    className="text-ink-muted group-hover:text-gold transition-all duration-500 group-hover:rotate-45"
                                />
                            </div>
                            <h3 className="font-serif text-3xl md:text-4xl text-ink-primary leading-tight tracking-tight">
                                {t(`landing.${p.key}_title`)}
                            </h3>
                            <p className="text-ink-secondary mt-5 leading-relaxed font-light">
                                {t(`landing.${p.key}_body`)}
                            </p>
                            {/* Hover gold line */}
                            <div className="absolute bottom-0 left-0 right-0 h-px bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ChapterThree({ t }) {
    const steps = [1, 2, 3];
    return (
        <section
            className="relative py-24 md:py-32 lg:py-40 border-t border-emerald-line/50 overflow-hidden"
            data-testid="chapter-03"
        >
            <div className="star-grid opacity-40" />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
                <ChapterHeader
                    n={CHAPTERS[2].n}
                    title={t(CHAPTERS[2].titleKey)}
                    headline={t(CHAPTERS[2].headline)}
                />

                {/* Vertical timeline steps */}
                <div className="mt-16 md:mt-24 relative max-w-4xl mx-auto">
                    {/* central rule */}
                    <motion.div
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.4, ease: EASE }}
                        className="rule-v absolute left-[26px] md:left-1/2 md:-translate-x-1/2 top-2 bottom-2"
                    />

                    <div className="space-y-16 md:space-y-24">
                        {steps.map((n, i) => (
                            <TimelineStep key={n} index={i} n={n} t={t} align={i % 2 === 0 ? "left" : "right"} />
                        ))}
                    </div>
                </div>

                {/* Rotating badge CTA */}
                <div className="mt-24 md:mt-32 flex justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, ease: EASE }}
                        className="relative"
                    >
                        {/* Rotating text ring */}
                        <svg
                            viewBox="0 0 200 200"
                            className="w-56 h-56 md:w-64 md:h-64 rotate-badge"
                            aria-hidden="true"
                        >
                            <defs>
                                <path
                                    id="circlePath"
                                    d="M 100, 100 m -80, 0 a 80,80 0 1,1 160,0 a 80,80 0 1,1 -160,0"
                                />
                            </defs>
                            <text fill="rgba(212,175,55,0.85)" fontSize="10" letterSpacing="6" fontFamily="Outfit">
                                <textPath href="#circlePath" startOffset="0">
                                    APPLY · MEMBERSHIP · SCOTTSCLUB · KUNGSTRÄDGÅRDEN · APPLY · MEMBERSHIP · SCOTTSCLUB ·
                                </textPath>
                            </text>
                        </svg>
                        <Link
                            to="/join"
                            data-testid="bottom-cta-join"
                            className="btn-gold absolute inset-0 m-auto w-32 h-32 md:w-36 md:h-36 rounded-full flex items-center justify-center text-center leading-tight text-xs tracking-[0.24em] uppercase font-medium hover:scale-105 transition-transform"
                        >
                            <span>
                                {t("landing.cta_primary").split(" ").slice(0, 2).join(" ")}
                                <br />
                                <ArrowUpRight size={16} className="inline mt-1" />
                            </span>
                        </Link>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function TimelineStep({ index, n, t, align }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });
    const isRight = align === "right";
    return (
        <div ref={ref} className="relative" data-testid={`step-${n}`}>
            {/* dot on rule */}
            <motion.div
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
                className="absolute left-[19px] md:left-1/2 md:-translate-x-1/2 top-3 w-3.5 h-3.5 rounded-full bg-gold shadow-[0_0_20px_rgba(212,175,55,0.7)] z-10"
            />

            {/* mobile — content stacks under dot */}
            <div className="md:hidden pl-16">
                <StepContent n={n} t={t} />
            </div>

            {/* desktop — alternating columns */}
            <div className="hidden md:grid md:grid-cols-2 md:gap-16">
                {!isRight && (
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                        className="text-right pr-4"
                    >
                        <StepContent n={n} t={t} align="right" />
                    </motion.div>
                )}
                {isRight && <div />}
                {isRight && (
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        animate={inView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                        className="text-left pl-4"
                    >
                        <StepContent n={n} t={t} align="left" />
                    </motion.div>
                )}
            </div>
        </div>
    );
}

function StepContent({ n, t, align = "left" }) {
    return (
        <div className={align === "right" ? "ml-auto" : ""}>
            <div className="font-serif italic text-gold text-lg mb-2">Step 0{n}</div>
            <p className="font-serif text-2xl md:text-3xl text-ink-primary leading-snug tracking-tight max-w-md">
                {t(`landing.step${n}`)}
            </p>
        </div>
    );
}

/* ---------- FOOTER ---------- */

function BigFooter({ t }) {
    return (
        <footer
            className="relative pt-24 pb-10 border-t border-emerald-line/60 overflow-hidden"
            data-testid="site-footer"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                {/* Massive wordmark */}
                <div className="line-mask">
                    <motion.div
                        initial={{ y: "110%" }}
                        whileInView={{ y: "0%" }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.2, ease: EASE }}
                        className="hero-word hero-word--stroke text-[22vw] md:text-[16vw] lg:text-[12rem] leading-[0.82] text-center"
                    >
                        ScottsClub
                    </motion.div>
                </div>

                <div className="mt-16 grid md:grid-cols-3 gap-8 items-start">
                    <div>
                        <div className="label-eyebrow">Location</div>
                        <p className="mt-3 text-ink-primary font-serif italic text-lg leading-snug">
                            John Scotts Kungsträdgården<br />
                            Hamngatan 19 · Stockholm
                        </p>
                    </div>
                    <div className="md:text-center">
                        <div className="label-eyebrow">Direct</div>
                        <p className="mt-3 text-ink-secondary text-sm leading-relaxed">
                            {t("landing.footer_note")}
                        </p>
                    </div>
                    <div className="md:text-right">
                        <div className="label-eyebrow">Ledger</div>
                        <p className="mt-3 text-ink-secondary text-sm leading-relaxed">
                            {t("landing.footer_copy")}
                        </p>
                        <Link
                            to="/privacy"
                            data-testid="footer-privacy"
                            className="mt-4 inline-flex items-center gap-1 text-ink-secondary hover:text-gold text-[11px] tracking-[0.28em] uppercase transition-colors"
                        >
                            Privacy · Integritet <ArrowUpRight size={12} />
                        </Link>
                        <div className="mt-2 flex gap-4 md:justify-end">
                            <Link
                                to="/admin/login"
                                data-testid="footer-admin"
                                className="inline-flex items-center gap-1 text-ink-muted hover:text-gold text-[10px] tracking-[0.32em] uppercase transition-colors"
                            >
                                Admin
                            </Link>
                            <Link
                                to="/staff/login"
                                data-testid="footer-staff"
                                className="inline-flex items-center gap-1 text-ink-muted hover:text-gold text-[10px] tracking-[0.32em] uppercase transition-colors"
                            >
                                Staff
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
}

/* ---------- PAGE ---------- */

export default function Landing() {
    const { t } = useT();
    useLenis();

    return (
        <div className="relative min-h-[100dvh] text-ink-primary" data-testid="landing-page">
            <Header floating />
            <PromoBanner />
            <Hero t={t} />
            <EventCountdown />
            <MarqueeStrip />
            <ChapterOne t={t} />
            <MembersWall />
            <ChapterTwo t={t} />
            <ChapterThree t={t} />
            <BigFooter t={t} />
        </div>
    );
}
